<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SuporteAmshiftAPI from 'dashboard/api/suporteAmshift';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampoDeAnexos from './CampoDeAnexos.vue';
import SituacaoDoChamado from './SituacaoDoChamado.vue';
import { useAnexos } from './useAnexos';
import {
  formatarDataHora,
  mensagemDoErro,
  montarFormDataDaResposta,
} from './helpers';

const props = defineProps({
  numero: { type: Number, required: true },
});

const emit = defineEmits(['voltar']);

const { t, locale } = useI18n();
const { anexos, recusas, adicionar, remover, limpar, aoColar } = useAnexos();

const chamado = ref(null);
const carregando = ref(true);
const erro = ref('');
const resposta = ref('');
const enviando = ref(false);

const temAnexos = computed(
  () =>
    chamado.value?.anexosDaAbertura?.length ||
    chamado.value?.interacoes?.some(interacao => interacao.anexos?.length)
);

const autor = interacao =>
  interacao.autorTipo === 'cliente'
    ? interacao.autorNome
    : t(`SUPORTE_AMSHIFT.DETALHE.AUTOR.${interacao.autorTipo}`);

const carregar = async () => {
  carregando.value = true;
  erro.value = '';
  try {
    const { data } = await SuporteAmshiftAPI.detalharChamado(props.numero);
    chamado.value = data;
  } catch (e) {
    erro.value = mensagemDoErro(e, t('SUPORTE_AMSHIFT.ERRO.GENERICO'));
  } finally {
    carregando.value = false;
  }
};

const responder = async () => {
  if (!resposta.value.trim() && !anexos.value.length) return;
  enviando.value = true;
  try {
    const { data } = await SuporteAmshiftAPI.responderChamado(
      props.numero,
      montarFormDataDaResposta(resposta.value, anexos.value)
    );
    chamado.value = data;
    resposta.value = '';
    limpar();
    useAlert(t('SUPORTE_AMSHIFT.DETALHE.RESPOSTA_ENVIADA'));
  } catch (e) {
    useAlert(mensagemDoErro(e, t('SUPORTE_AMSHIFT.ERRO.GENERICO')));
  } finally {
    enviando.value = false;
  }
};

onMounted(carregar);
</script>

<template>
  <div class="flex flex-col gap-4">
    <Button
      type="button"
      icon="i-lucide-arrow-left"
      variant="link"
      color="slate"
      size="sm"
      class="self-start"
      :label="t('SUPORTE_AMSHIFT.DETALHE.VOLTAR')"
      @click="emit('voltar')"
    />

    <div v-if="carregando" class="flex justify-center py-8"><Spinner /></div>
    <p
      v-else-if="erro"
      class="px-3 py-2 mb-0 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
    >
      {{ erro }}
    </p>

    <template v-else-if="chamado">
      <div class="flex flex-col gap-2">
        <div class="flex flex-wrap items-start justify-between gap-2">
          <h4 class="mb-0 text-base font-medium text-n-slate-12">
            <span class="text-n-slate-10">#{{ chamado.numero }}</span>
            {{ chamado.titulo }}
          </h4>
          <SituacaoDoChamado :chamado="chamado" />
        </div>
        <p class="mb-0 text-xs text-n-slate-10">
          {{
            t('SUPORTE_AMSHIFT.DETALHE.ABERTO_POR', {
              nome: chamado.solicitante?.nome,
              data: formatarDataHora(chamado.criadoEm, locale),
            })
          }}
          <template v-if="chamado.responsavel">
            ·
            {{
              t('SUPORTE_AMSHIFT.DETALHE.RESPONSAVEL', {
                nome: chamado.responsavel.nome,
              })
            }}
          </template>
        </p>
        <p class="mb-0 text-sm whitespace-pre-wrap text-n-slate-12">
          {{ chamado.descricao }}
        </p>
        <span
          v-for="anexo in chamado.anexosDaAbertura"
          :key="anexo.id"
          class="flex items-center gap-1 text-xs text-n-slate-11"
          data-test="anexo-da-abertura"
        >
          <span class="i-lucide-paperclip size-3" />{{ anexo.nome }}
        </span>
      </div>

      <ul class="flex flex-col gap-2 overflow-y-auto max-h-[40vh]">
        <li
          v-for="interacao in chamado.interacoes"
          :key="interacao.id"
          class="flex flex-col gap-1 px-3 py-2 rounded-lg"
          :class="
            interacao.autorTipo === 'cliente' ? 'bg-n-alpha-1' : 'bg-n-blue-2'
          "
        >
          <span class="text-xs text-n-slate-10">
            {{ autor(interacao) }} ·
            {{ formatarDataHora(interacao.criadoEm, locale) }}
          </span>
          <span class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ interacao.corpo }}
          </span>
          <span
            v-for="anexo in interacao.anexos"
            :key="anexo.id"
            class="flex items-center gap-1 text-xs text-n-slate-11"
          >
            <span class="i-lucide-paperclip size-3" />{{ anexo.nome }}
          </span>
        </li>
        <li v-if="!chamado.interacoes?.length" class="text-sm text-n-slate-11">
          {{ t('SUPORTE_AMSHIFT.DETALHE.SEM_MENSAGENS') }}
        </li>
      </ul>
      <p v-if="temAnexos" class="mb-0 text-xs text-n-slate-10">
        {{ t('SUPORTE_AMSHIFT.DETALHE.ANEXOS_NA_CENTRAL') }}
      </p>

      <div class="flex flex-col gap-3" @paste="aoColar">
        <TextArea
          v-model="resposta"
          :label="t('SUPORTE_AMSHIFT.DETALHE.RESPONDER')"
          :placeholder="t('SUPORTE_AMSHIFT.DETALHE.RESPOSTA_PLACEHOLDER')"
          :max-length="20000"
          auto-height
        />
        <CampoDeAnexos
          :anexos="anexos"
          :recusas="recusas"
          :disabled="enviando"
          @adicionar="adicionar"
          @remover="remover"
        />
        <Button
          type="button"
          icon="i-lucide-send"
          class="self-end"
          :label="t('SUPORTE_AMSHIFT.DETALHE.ENVIAR_RESPOSTA')"
          :is-loading="enviando"
          :disabled="enviando || (!resposta.trim() && !anexos.length)"
          @click="responder"
        />
      </div>
    </template>
  </div>
</template>
